import Dialog from "@mui/material/Dialog";
import { Formik, FormikProps } from "formik";
import { useEffect, useRef, useState, type FC } from "react";
import type { StoreAccountAssignment, StoreAndPartnerSelection } from "../vc-account-assignments-types";
import { AssignmentConfirmationForm } from "./AssignmentConfirmationForm";
import { AssignmentSuccessScreen } from "./AssignmentSuccessScreen";
import { StoreAndPartnerSelectionForm } from "./StoreAndPartnerSelectionForm";

export interface NewVcAccountAssignmentsDialogProps {
  isOpen: boolean;
  onClose(): void;
}

const defaultStoreAndPartnerSelection: StoreAndPartnerSelection = {
  vendorName: "",
  storeNames: [],
  partnerId: null,
};

export const NewVcAccountAssignmentsDialog: FC<NewVcAccountAssignmentsDialogProps> = ({ isOpen, onClose }) => {
  const [storeAndPartnerSelection, setStoreAndPartnerSelection] = useState<StoreAndPartnerSelection | null>(null);
  const [confirmedAssignments, setConfirmedAssignments] = useState<StoreAccountAssignment[] | null>(null);

  const clearState = () => {
    setStoreAndPartnerSelection(null);
    setConfirmedAssignments(null);
  };

  const clearStateAndClose = () => {
    clearState();
    onClose();
  };

  const formikRef = useRef<FormikProps<StoreAndPartnerSelection> | null>(null);

  useEffect(() => {
    if (!isOpen) {
      clearState();
      formikRef.current?.resetForm();
    }
  }, [isOpen]);

  return (
    <>
      {/* Formik is declared at this level so that it can manage the dismissability of the dialog in the first step */}
      <Formik
        innerRef={formikRef}
        initialValues={defaultStoreAndPartnerSelection}
        onSubmit={newStoreAndPartnerSelection => {
          setStoreAndPartnerSelection(newStoreAndPartnerSelection);
        }}
      >
        {({ dirty, resetForm }) => (
          <Dialog
            fullWidth
            maxWidth="md"
            open={isOpen && !storeAndPartnerSelection}
            onClose={dirty ? undefined : clearStateAndClose}
          >
            <StoreAndPartnerSelectionForm
              onCancel={() => {
                resetForm();
                clearStateAndClose();
              }}
            />
          </Dialog>
        )}
      </Formik>

      <Dialog
        fullWidth
        maxWidth="md"
        open={isOpen && storeAndPartnerSelection !== null && confirmedAssignments === null}
        onClose={undefined}
      >
        {storeAndPartnerSelection && (
          <AssignmentConfirmationForm
            storeAndPartnerSelection={storeAndPartnerSelection}
            clearStoreAndPartnerSelection={() => setStoreAndPartnerSelection(null)}
            setConfirmedAssignments={setConfirmedAssignments}
          />
        )}
      </Dialog>

      <Dialog fullWidth maxWidth="md" open={isOpen && confirmedAssignments !== null} onClose={undefined}>
        {confirmedAssignments && (
          <AssignmentSuccessScreen confirmedAssignments={confirmedAssignments} onDismiss={clearStateAndClose} />
        )}
      </Dialog>
    </>
  );
};
